import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Target, Trophy, Share2, Flame, ImageDown } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import BadgePreviewDialog from "@/components/BadgePreviewDialog";

const TARGETS = [50, 60, 70, 80, 90, 100];

export default function GoalCard({ refreshKey }) {
  const [goal, setGoal] = useState(null);
  const [badgeOpen, setBadgeOpen] = useState(false);

  const load = async () => {
    const { data } = await api.get("/goal");
    setGoal(data);
  };

  useEffect(() => { load(); }, [refreshKey]);

  const changeTarget = async (v) => {
    try {
      const { data } = await api.put("/goal", { target: parseInt(v, 10) });
      setGoal(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const share = async () => {
    const text = `J'ai sauvé ${goal.rate}% de mes produits ce mois-ci avec FoodFend et atteint mon objectif anti-gaspi de ${goal.target}% ! 🌱`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Mon badge anti-gaspi FoodFend", text });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success("Badge copié dans le presse-papier !");
      }
    } catch {
      /* user cancelled share */
    }
  };

  if (!goal) return null;

  return (
    <div
      className={cn(
        "rounded-2xl border p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-colors",
        goal.achieved ? "border-primary bg-primary/[0.06]" : "bg-card border-border"
      )}
      data-testid="goal-card"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          {goal.achieved ? (
            <Trophy className="h-5 w-5 text-primary" />
          ) : (
            <Target className="h-5 w-5 text-muted-foreground" />
          )}
          <div>
            <h3 className="font-display text-xl leading-tight">Objectif anti-gaspi</h3>
            <p className="text-xs text-muted-foreground capitalize">{goal.month_label}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="mono text-[10px] uppercase tracking-mono text-muted-foreground">Cible</span>
          <Select value={String(goal.target)} onValueChange={changeTarget}>
            <SelectTrigger className="h-8 w-[92px]" data-testid="goal-target-trigger">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card">
              {TARGETS.map((t) => (
                <SelectItem key={t} value={String(t)}>{t}%</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-4">
        <div className="flex items-baseline justify-between mb-1.5">
          <span className="text-sm text-muted-foreground">
            {goal.total === 0
              ? "Aucun produit traité ce mois-ci"
              : `${goal.saved} sauvé(s) sur ${goal.total} traité(s)`}
          </span>
          <span className={cn("mono font-bold text-lg", goal.achieved ? "text-primary" : "text-foreground")}>
            {goal.rate}%
          </span>
        </div>
        <Progress value={goal.rate} className="h-2" data-testid="goal-progress" />
      </div>

      {goal.achieved && (
        <div className="mt-4 flex items-center justify-between gap-3 rounded-md bg-primary/10 border border-primary/25 px-4 py-3">
          <div className="flex items-center gap-2 min-w-0">
            <Trophy className="h-4 w-4 text-primary shrink-0" />
            <p className="text-sm font-semibold text-primary truncate">
              Objectif atteint — vous êtes un vrai sauveur !
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={share}
            data-testid="goal-share-btn"
            className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 px-3 py-1.5 text-xs font-semibold text-primary transition-colors hover:bg-primary/10 shrink-0"
          >
            <Share2 className="h-3.5 w-3.5" /> Partager
          </button>
          <button
            onClick={() => setBadgeOpen(true)}
            data-testid="goal-badge-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-transform hover:-translate-y-[1px] shrink-0"
          >
            <ImageDown className="h-3.5 w-3.5" /> Badge
          </button>
          </div>
        </div>
      )}

      {goal.history && goal.history.length > 0 && (
        <div className="mt-5 pt-4 border-t border-border" data-testid="goal-history">
          <div className="flex items-center justify-between mb-3">
            <span className="mono text-[10px] uppercase tracking-mono text-muted-foreground">
              6 derniers mois
            </span>
            {goal.streak > 0 && (
              <span className="inline-flex items-center gap-1 mono text-xs font-bold text-[hsl(var(--warning))]"
                data-testid="goal-streak">
                <Flame className="h-3.5 w-3.5" /> Série de {goal.streak} mois
              </span>
            )}
          </div>
          <div className="flex items-end justify-between gap-2">
            {goal.history.map((m) => (
              <div key={m.month} className="flex-1 flex flex-col items-center gap-1.5" title={`${m.rate}% (${m.saved}/${m.total})`}>
                <div className="w-full h-16 rounded-sm bg-muted relative overflow-hidden flex items-end">
                  <div
                    className={cn("w-full transition-[height]", m.achieved ? "bg-primary" : m.total ? "bg-[hsl(var(--warning))]" : "bg-border")}
                    style={{ height: `${m.total ? Math.max(m.rate, 6) : 0}%` }}
                  />
                  {m.achieved && (
                    <Trophy className="h-3 w-3 text-primary-foreground absolute top-1 left-1/2 -translate-x-1/2" />
                  )}
                </div>
                <span className="mono text-[10px] text-muted-foreground">{m.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <BadgePreviewDialog open={badgeOpen} onOpenChange={setBadgeOpen} goal={goal} />
    </div>
  );
}
